import { localizeHolidayName } from '../../../../../../../common/holidays.js'
import { t } from '../../../../../../../common/i18n.js'
import type { Language } from '../../../../../../../common/types/i18n.js'
import { formatDisplayDate } from '../../../../../../../common/utils/format-utils.js'
import { Button } from '../../../../../../components/button/button.js'
import {
    CalendarRange,
    Globe,
    Pencil,
    ShieldCheck,
} from '../../../../../../components/icon/solar-icons.js'
import { Section, SectionHeader } from '../../../../../../components/section/section.js'
import type { TimeAwayDetails } from './time-away-derive.js'

interface TimeAwayDetailsCardProps {
    details: TimeAwayDetails
    nextHoliday: { date: string; name: string } | null
    lang: Language
    // TASK-348: opens the leave-policy editor; only passed for viewers who
    // hold the time-away settings permission.
    onEditPolicy?: () => void
}

// Facts about the employee's leave setup: assigned vacation policy, how
// requests get approved, and the observed public-holiday calendar. The policy
// is editable in place for time-away settings managers (TASK-348).
export const TimeAwayDetailsCard = ({
    details,
    nextHoliday,
    lang,
    onEditPolicy,
}: TimeAwayDetailsCardProps) => {
    const tOff = t.portal.timeOff
    const view = tOff.view

    const approvalLabel =
        details.approvalMode === 'manager'
            ? view.approvalManager(lang)
            : details.approvalMode === 'automatic'
              ? view.approvalAutomatic(lang)
              : '–'

    return (
        <Section className="time-away__details-card">
            <SectionHeader
                title={view.detailsTitle(lang)}
                actions={
                    onEditPolicy ? (
                        <Button
                            variant="tertiary"
                            size="small"
                            iconLeft={Pencil}
                            text={view.policyEdit(lang)}
                            onClick={onEditPolicy}
                        />
                    ) : undefined
                }
            />
            <div className="time-away__detail-row">
                <span className="time-away__detail-icon">
                    <CalendarRange size={15} />
                </span>
                <span className="time-away__detail-label">{tOff.policy(lang)}</span>
                <span className="time-away__detail-value">
                    {details.policyName ?? '–'}
                    {details.toovabaAssigned && (
                        <span className="time-away__detail-note">{view.toovabaNote(lang)}</span>
                    )}
                </span>
            </div>
            <div className="time-away__detail-row">
                <span className="time-away__detail-icon">
                    <ShieldCheck size={15} />
                </span>
                <span className="time-away__detail-label">{view.approvalFlow(lang)}</span>
                <span className="time-away__detail-value">{approvalLabel}</span>
            </div>
            <div className="time-away__detail-row">
                <span className="time-away__detail-icon">
                    <Globe size={15} />
                </span>
                <span className="time-away__detail-label">{tOff.holidays.title(lang)}</span>
                <span className="time-away__detail-value">
                    {view.holidayCountry(lang)}
                    {nextHoliday && (
                        <span className="time-away__detail-note">
                            {view.nextHoliday(
                                localizeHolidayName(nextHoliday.date, nextHoliday.name, lang),
                                formatDisplayDate(nextHoliday.date, 'long', lang),
                            )(lang)}
                        </span>
                    )}
                </span>
            </div>
        </Section>
    )
}
